// src/features/blogs/blogsApiSlice.js
import { createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

const blogsAdapter = createEntityAdapter({
  selectId: (blog) => blog._id,
  sortComparer: (a, b) => b.createdAt?.localeCompare(a.createdAt),
});

const initialState = blogsAdapter.getInitialState();

export const blogsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // PUBLIC
    getPublicBlogs: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => {
        console.log('Fetching blogs with:', { page, limit });
        return `/blogs?page=${page}&limit=${limit}`;
      },
      transformResponse: (responseData) => {
        console.log('Transforming response data:', responseData);

        // Handle the new response structure where data is the array of blogs
        if (!responseData || !responseData.data) {
          return {
            ids: [],
            entities: {},
            totalDocs: 0,
            page: 1,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
            docs: []
          }
        }

        const transformed = {
          ids: responseData.data.map(blog => blog._id),
          entities: responseData.data.reduce((acc, blog) => {
            acc[blog._id] = blog
            return acc
          }, {}),
          totalDocs: responseData.total || 0,
          page: responseData.page || 1,
          totalPages: Math.ceil((responseData.total || 0) / (responseData.limit || 10)),
          hasNextPage: responseData.hasNext || false,
          hasPrevPage: responseData.hasPrev || false,
          docs: responseData.data // Store the original array
        };
        
        console.log('Transformed response:', transformed);
        return transformed;
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: 'Blog', id: 'LIST' },
            ...result.ids.map(id => ({ type: 'Blog', id }))
          ]
        }
        return [{ type: 'Blog', id: 'LIST' }]
      }
    }),

    getBlogBySlug: builder.query({
      query: (slug) => `/blogs/slug/${slug}`,
      transformResponse: (blog) => ({
        ids: [blog._id],
        entities: { [blog._id]: blog },
      }),
      providesTags: (result) => [{ type: 'Blog', id: result?.ids[0] }],
    }),

    // ADMIN
    getAdminBlogs: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => `/blogs/admin?page=${page}&limit=${limit}`,
      transformResponse: (responseData) => {
        console.log('Admin blogs response:', responseData); // Debug log

        // Handle the new response structure where data is the array of blogs
        if (!responseData || !responseData.data) {
          return {
            ids: [],
            entities: {},
            totalDocs: 0,
            page: 1,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
            docs: []
          }
        }

        const transformed = {
          ids: responseData.data.map(blog => blog._id),
          entities: responseData.data.reduce((acc, blog) => {
            acc[blog._id] = blog
            return acc
          }, {}),
          totalDocs: responseData.total || 0,
          page: responseData.page || 1,
          totalPages: Math.ceil((responseData.total || 0) / (responseData.limit || 20)),
          hasNextPage: responseData.hasNext || false,
          hasPrevPage: responseData.hasPrev || false,
          docs: responseData.data // Store the original array for direct access
        };
        
        console.log('Transformed admin response:', transformed); // Debug log
        return transformed;
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [
            { type: 'Blog', id: 'LIST' },
            ...result.ids.map(id => ({ type: 'Blog', id }))
          ]
        }
        return [{ type: 'Blog', id: 'LIST' }]
      }
    }),

    getBlogById: builder.query({
      query: (id) => `/blogs/${id}`,
      transformResponse: (blog) => ({
        ids: [blog._id],
        entities: { [blog._id]: blog },
      }),
      providesTags: (result, error, id) => [{ type: 'Blog', id }],
    }),

    createBlog: builder.mutation({
      query: (payload) => ({
        url: '/blogs',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Blog', id: 'LIST' }],
    }),

    updateBlog: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/blogs/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Blog', id },
        { type: 'Blog', id: 'LIST' },
      ],
    }),

    deleteBlog: builder.mutation({
      query: (id) => ({
        url: `/blogs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Blog', id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
});

// Selectors
export const selectBlogsResult = blogsApiSlice.endpoints.getPublicBlogs.select();
export const selectAdminBlogsResult = blogsApiSlice.endpoints.getAdminBlogs.select();

// Creates memoized selector
const selectBlogsData = createSelector(
  selectBlogsResult,
  (blogsResult) => blogsResult.data ?? initialState
);

const selectAdminBlogsData = createSelector(
  selectAdminBlogsResult,
  (blogsResult) => blogsResult.data ?? initialState
);

// getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllBlogs,
  selectById: selectBlogById,
  selectIds: selectBlogIds,
} = blogsAdapter.getSelectors((state) => selectBlogsData(state) ?? initialState);

export const {
  useGetPublicBlogsQuery,
  useGetBlogBySlugQuery,
  useGetAdminBlogsQuery,
  useGetBlogByIdQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
} = blogsApiSlice;
